import contentService from '../services/contentService';
import { addBreadcrumb } from '../sentry';
import { CONTENT_ACTION_TYPES } from './types';

function sanitizeBreadcrumbError(error) {
  if (error == null) return undefined;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error?.message === 'string') return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function breadcrumbAddCreatorCategoriesFailure(error) {
  const detail = sanitizeBreadcrumbError(error);
  addBreadcrumb({
    category: 'content.categories',
    message: 'add creator categories failed',
    level: 'warning',
    ...(detail != null && { data: { error: detail } }),
  });
}

/**
 * Fetch content creator list with unified filtering
 * @param {Object} params - Query parameters
 * @param {string} params.searchQuery - Search query string
 * @param {number} params.limit - Number of creators to fetch (default: 5, ignored if searchQuery exists)
 * @param {boolean} params.isTrending - Whether to fetch trending creators (default: false)
 * @param {boolean} params.isFeature - Whether to fetch featured creators (default: false)
 * @param {string} params.userId - User ID
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const getContentCreatorList = async (params = {}) => {
    try {
        const response = await contentService.getContentCreatorList(params);
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Fetch content creators
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of creators to fetch
 * @param {number} params.skip - Number of creators to skip
 * @param {string} params.sortBy - Sort order
 * @param {boolean} params.isGuest - Whether user is guest or authenticated
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const fetchContentCreators = async (params = {}) => {
    try {
        const response = await contentService.getContentCreators(params);
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Search content creators
 * @param {string} query - Search query string
 * @param {Object} options - Optional parameters
 * @param {number} options.limit - Number of creators to fetch (default: 10)
 * @param {number} options.skip - Number of creators to skip (default: 0)
 * @param {string} options.sortBy - Sort order (default: 'username_asc')
 * @returns {Promise<Object>} Promise that resolves to response object with search results
 */
export const searchContentCreators = async (query, options = {}) => {
    try {
        const { limit = 10, skip = 0, sortBy = 'username_asc' } = options;

        const response = await contentService.getContentCreators({
            search: query,
            limit,
            skip,
            sortBy
        });

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Add creator categories to user's category list
 * @param {Object} creatorData - Creator data object
 * @param {Array} userCategories - Current user categories (optional)
 * @returns {Promise<Object>} Promise that resolves to response object with added categories info
 */
export const addCreatorCategories = async (creatorData, userCategories = []) => {
    try {
        const response = await contentService.addCreatorCategories(creatorData, userCategories);
        if (response.success) {
            return { 
                success: true, 
                data: response.data,
                addedCategories: response.data?.addedCategories || []
            };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Fire-and-forget wrapper for {@link addCreatorCategories}.
 * Does not block navigation; failures are Sentry breadcrumbs only (not issues).
 */
export const fireAndForgetAddCreatorCategories = (creatorData, userCategories = []) => {
    addCreatorCategories(creatorData, userCategories)
        .then((result) => {
            if (!result?.success) {
                breadcrumbAddCreatorCategoriesFailure(result?.error);
            }
        })
        .catch((error) => {
            breadcrumbAddCreatorCategoriesFailure(error);
        });
};

/**
 * Fetch content by creator
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {string} creatorId - Creator ID
 * @param {string} type - Content type (feed, media, free)
 * @param {number} limit - Number of items to fetch
 * @param {number} skip - Number of items to skip (page * limit)
 * @param {string} filter - Optional filter parameter
 * @param {Function} callback - Callback function to handle the response
 * @returns {Function} Redux thunk action
 */
export const fetchContentByCreator = (isAuthenticated, creatorId, type, limit, skip, filter, callback) => async (dispatch) => {
    try {
        const response = await contentService.getContentByCreator(isAuthenticated, creatorId, type, limit, skip, filter);

        if (callback) {
            callback(response);
        }

        return response;
    } catch (error) {
        const errorResponse = {
            success: false,
            error: true,
            message: error.message || 'Failed to fetch content',
            data: null
        };

        if (callback) {
            callback(errorResponse);
        }

        return errorResponse;
    }
};

/**
 * Fetch content counts for a creator
 * @param {string} contentCreatorId - Creator ID
 * @returns {Promise<Object>} Content counts result
 */
export const fetchContentCounts = async (contentCreatorId) => {
    try {
        if (!contentCreatorId) {
            return { success: false, error: 'Content creator ID is required' };
        }

        const response = await contentService.fetchContentCounts(contentCreatorId);

        if (response.success && response.data) {
            const { freeContent, paidContent } = response.data;

            // Calculate content counts based on API response
            const contentCounts = {
                freeContent: freeContent || 0,
                paidContent: paidContent || 0,
                // Feed should be sum of freeContent and paidContent
                feedCount: (freeContent || 0) + (paidContent || 0),
                // Media should use paidContent count
                mediaCount: paidContent || 0,
                // Free content uses freeContent count
                freeContentCount: freeContent || 0
            };

            return { success: true, data: contentCounts };
        } else {
            return { success: false, error: response.message || 'Failed to fetch content counts' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Fetch single content details by creator and content id
 * @param {string} creatorId - Creator ID
 * @param {string} contentId - Content ID
 * @returns {Promise<Object>} Content response
 */
export const fetchCreatorContentById = async (creatorId, contentId) => {
    try {
        if (!creatorId) {
            return { success: false, error: 'Creator ID is required' };
        }

        if (!contentId) {
            return { success: false, error: 'Content ID is required' };
        }

        const response = await contentService.getCreatorContentById(creatorId, contentId);

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message || 'Failed to fetch content' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'Failed to fetch content' };
    }
};

/**
 * Update a single item in the feed store
 * @param {Object|null} post - Updated post data
 * @returns {{type: string, payload: Object|null}} Redux action
 */
export const updateFeedItem = (post) => ({
    type: CONTENT_ACTION_TYPES.UPDATE_FEED_ITEM,
    payload: post,
});

/**
 * Delete content by id
 * @param {string} contentId
 * @param {Function} callback
 * @returns {Function} Redux thunk action
 */
export const deleteContent = (contentId, callback) => async (dispatch) => {
    try {
        const response = await contentService.deleteContent(contentId);
        if (callback) callback(response);
        return response;
    } catch (error) {
        const errorResponse = {
            success: false,
            error: true,
            message: error.message || 'Failed to delete content',
            data: null,
        };
        if (callback) callback(errorResponse);
        return errorResponse;
    }
};

/**
 * Update content by id
 * @param {string} contentId
 * @param {Object} data
 * @param {Function} callback
 * @returns {Function} Redux thunk action
 */
export const updateContent = (contentId, data, callback) => async (dispatch) => {
    try {
        const response = await contentService.updateContent(contentId, data);
        if (callback) callback(response);
        return response;
    } catch (error) {
        const errorResponse = {
            success: false,
            error: true,
            message: error.message || 'Failed to update content',
            data: null,
        };
        if (callback) callback(errorResponse);
        return errorResponse;
    }
};

/**
 * Fetch feed action
 * @param {number} limit - Number of items to fetch (default: 9)
 * @param {number} page - Page number (0-indexed, default: 0)
 * @param {boolean} isLoadMore - Whether this is a load more request (default: false)
 * @returns {Function} Redux thunk action
 */
export const fetchFeed = (limit = 9, page = 0, isLoadMore = false) => async (dispatch) => {
    dispatch({ type: CONTENT_ACTION_TYPES.FETCH_FEED_REQUEST });
    
    try {
        // Validate parameters
        if (limit < 1) {
            throw new Error('Limit must be greater than 0');
        }

        if (page < 0) {
            throw new Error('Page must be greater than or equal to 0');
        }

        // Calculate skip value from page number (skip = page * limit)
        // const skip = page * limit;
        const currentPage = page;
        
        const response = await contentService.getFeed(limit, currentPage);

        if (response.success) {
            dispatch({
                type: CONTENT_ACTION_TYPES.FETCH_FEED_SUCCESS,
                payload: {
                    data: response.data?.data || [],
                    recordsTotal: response.data?.recordsTotal || 0,
                    message: response.message || 'Feed fetched successfully',
                    limit: limit,
                    page: page, // Use the page number passed to the action
                    isLoadMore: isLoadMore
                }
            });

            return {
                success: true,
                data: response.data?.data || [],
                recordsTotal: response.data?.recordsTotal || 0
            };
        } else {
            dispatch({
                type: CONTENT_ACTION_TYPES.FETCH_FEED_FAILURE,
                payload: {
                    error: response.message || 'Failed to fetch feed',
                    status: response.status || 400
                }
            });

            return {
                success: false,
                error: response.message || 'Failed to fetch feed'
            };
        }
    } catch (error) {
        const errorMessage = error.message || 'An unexpected error occurred while fetching feed';
        const errorStatus = error.status || error.response?.status || 500;

        dispatch({
            type: CONTENT_ACTION_TYPES.FETCH_FEED_FAILURE,
            payload: {
                error: errorMessage,
                status: errorStatus
            }
        });

        return {
            success: false,
            error: errorMessage
        };
    }
};

/**
 * Fetch content by creator
 * @param {string} creatorId - Creator ID
 * @param {string} type - Content type (feed, media, free)
 * @param {number} limit - Number of items to fetch
 * @param {number} skip - Number of items to skip (page * limit)
 * @param {string} filter - Optional filter parameter
 * @param {Function} callback - Callback function to handle the response
 * @returns {Function} Redux thunk action
 */
export const fetchFeedOfCreator = (creatorId, type, limit, skip, filter, callback) => async (dispatch) => {
    try {
        const response = await contentService.getFeedssfCreator(creatorId, type, limit, skip, filter);

        if (callback) {
            callback(response);
        }

        return response;
    } catch (error) {
        const errorResponse = {
            success: false,
            error: true,
            message: error.message || 'Failed to fetch content',
            data: null
        };

        if (callback) {
            callback(errorResponse);
        }

        return errorResponse;
    }
};

/**
 * Fetch purchased content action
 * @param {number} limit - Number of items to fetch (default: 6)
 * @param {number} page - Page number (default: 0)
 * @param {boolean} isLoadMore - Whether this is a load more request (default: false)
 * @returns {Function} Redux thunk action
 */
export const fetchPurchasedContent = (limit = 6, page = 0, isLoadMore = false) => async (dispatch) => {
    dispatch({ type: CONTENT_ACTION_TYPES.FETCH_PURCHASED_CONTENT_REQUEST });
    
    try {
        // Validate parameters
        if (limit < 1) {
            throw new Error('Limit must be greater than 0');
        }

        if (page < 0) {
            throw new Error('Page must be greater than or equal to 0');
        }

        const response = await contentService.getPurchasedContent(limit, page);

        if (response.success) {
            dispatch({
                type: CONTENT_ACTION_TYPES.FETCH_PURCHASED_CONTENT_SUCCESS,
                payload: {
                    data: response.data?.data || [],
                    message: response.message || 'Purchased content fetched successfully',
                    limit: limit,
                    page: page, // Use the page number passed to the action
                    isLoadMore: isLoadMore
                }
            });

            return {
                success: true,
                data: response.data?.data || []
            };
        } else {
            dispatch({
                type: CONTENT_ACTION_TYPES.FETCH_PURCHASED_CONTENT_FAILURE,
                payload: {
                    error: response.message || 'Failed to fetch purchased content',
                    status: response.status || 400
                }
            });

            return {
                success: false,
                error: response.message || 'Failed to fetch purchased content'
            };
        }
    } catch (error) {
        const errorMessage = error.message || 'An unexpected error occurred while fetching purchased content';
        const errorStatus = error.status || error.response?.status || 500;

        dispatch({
            type: CONTENT_ACTION_TYPES.FETCH_PURCHASED_CONTENT_FAILURE,
            payload: {
                error: errorMessage,
                status: errorStatus
            }
        });

        return {
            success: false,
            error: errorMessage
        };
    }
};